export function getDecimalsFromTickSize(tickSize) {
  const tickSizeNum = Number(tickSize);
  if (!tickSizeNum || tickSizeNum <= 0 || Number.isNaN(tickSizeNum)) return 2;
  if (tickSizeNum < 1) {
    return Math.max(0, Math.ceil(-Math.log10(tickSizeNum)));
  }
  return 0;
}

export function formatPrice(price, tickSize) {
  try {
    const num = typeof price === "string" ? Number(price) : price;
    if (typeof num === "number" && !Number.isNaN(num) && Number.isFinite(num)) {
      const decimals = getDecimalsFromTickSize(tickSize);
      return num.toFixed(decimals);
    }
    return "0.00";
  } catch {
    return "0.00";
  }
}

export function formatNumber(data, decimal = 2) {
  try {
    const num = typeof data === "string" ? Number(data) : data;
    if (typeof num === "number" && !Number.isNaN(num) && Number.isFinite(num)) {
      return num.toFixed(decimal);
    }
    return "0.00";
  } catch {
    return "0.00";
  }
}

/** Compact USD-style volume (e.g. 637.1M) */
export function formatVolumeLabel(volume) {
  const n = typeof volume === "string" ? Number(volume) : volume;
  if (typeof n !== "number" || Number.isNaN(n) || !Number.isFinite(n)) return "0.00";
  const abs = Math.abs(n);
  if (abs >= 1e12) return `${(n / 1e12).toFixed(2)}T`;
  if (abs >= 1e9) return `${(n / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `${(n / 1e6).toFixed(2)}M`;
  if (abs >= 1e3) return `${(n / 1e3).toFixed(2)}K`;
  return n.toFixed(2);
}

export function displayChangePercent(changePercentage) {
  const v = Number(changePercentage);
  if (Number.isNaN(v) || !Number.isFinite(v)) return "0.00%";
  const s = v > 0 ? `+${formatNumber(v, 2)}%` : `${formatNumber(v, 2)}%`;
  return s;
}

/** Spot pair % change: backend may send `change` or `change_percentage` (same as reference project). */
export function spotChangeNumber(item) {
  const n = Number(item?.change_percentage ?? item?.change);
  if (Number.isNaN(n) || !Number.isFinite(n)) return 0;
  return n;
}

export function displaySpotChangePercent(item) {
  return displayChangePercent(item?.change_percentage ?? item?.change);
}

export function isPositiveChange(item) {
  return Number(item?.change_percentage ?? item?.change ?? 0) >= 0;
}

function parseMarketNumber(value) {
  if (value === undefined || value === null || value === "") return null;
  const n = typeof value === "string" ? Number(value) : Number(value);
  return Number.isFinite(n) ? n : null;
}

const FUTURES_ASSET_NAMES = {
  BTC: "Bitcoin",
  ETH: "Ethereum",
  BNB: "Binance",
};

function resolveFutures24hRange(item, buyPrice, changePct) {
  let high = parseMarketNumber(item.high) ?? parseMarketNumber(item.high_24h);
  let low = parseMarketNumber(item.low) ?? parseMarketNumber(item.low_24h);
  let open =
    parseMarketNumber(item.open) ??
    parseMarketNumber(item.open_24h) ??
    parseMarketNumber(item.open_price);

  const price = buyPrice;
  if (price == null || price <= 0) return { high, low, open };

  const isInvalidBand = (v) => v == null || v === 0;
  if (isInvalidBand(high) && isInvalidBand(low)) {
    const pct = Math.abs(changePct ?? 0) / 100;
    const band = Math.max(pct, 0.005) * price;
    high = price + band;
    low = Math.max(price - band, 0);
  }

  if (isInvalidBand(open)) {
    const pct = (changePct ?? 0) / 100;
    open = pct !== -1 ? price / (1 + pct) : price;
  }

  return { high, low, open };
}

/** Map socket futures contract rows to the market-table shape. */
export function normalizeFuturesMarketPair(item) {
  if (!item || typeof item !== "object") return item;

  const base = String(item.short_name || item.base_currency || item.base_asset || "").trim();
  const quote = String(
    item.margin_asset || item.marginAsset || item.quote_asset || item.quote_currency || item.quote || "USDT"
  ).trim();

  const buyPrice =
    parseMarketNumber(item.buy_price) ??
    parseMarketNumber(item.last_price) ??
    parseMarketNumber(item.mark_price) ??
    parseMarketNumber(item.bid_price);

  const sellPrice =
    parseMarketNumber(item.sell_price) ??
    parseMarketNumber(item.ask_price) ??
    parseMarketNumber(item.mark_price) ??
    parseMarketNumber(item.last_price);

  const changePercentage =
    parseMarketNumber(item.change_percentage) ??
    parseMarketNumber(item.changePercent) ??
    parseMarketNumber(item.price_change_24h);

  const { high, low, open } = resolveFutures24hRange(item, buyPrice, changePercentage);

  const volume =
    parseMarketNumber(item.volume) ??
    parseMarketNumber(item.volume_24h_quote) ??
    parseMarketNumber(item.volume_24h);

  const tickSize = item.tick_size ?? item.tickSize ?? null;
  const pricePrecision = item.price_precision ?? item.pricePrecision ?? null;

  const displayName = String(item.display_name || item.base_currency_fullname || item.name || "").trim();
  const name = displayName || FUTURES_ASSET_NAMES[base] || base;

  return {
    ...item,
    short_name: base,
    base_currency: base,
    base_asset: base,
    quote_currency: quote,
    quote_asset: quote,
    margin_asset: item.margin_asset || item.marginAsset || quote,
    base_currency_fullname: name,
    buy_price: buyPrice,
    sell_price: sellPrice,
    high,
    low,
    open,
    open_24h: open,
    volume,
    change_percentage: changePercentage,
    tick_size: tickSize,
    tickSize,
    price_precision: pricePrecision,
  };
}

export function normalizeFuturesMarketPairs(pairs) {
  if (!Array.isArray(pairs)) return [];
  return pairs.map(normalizeFuturesMarketPair);
}

/** Keep only USDⓈ-M contracts from a `futures:update` contracts snapshot. */
export function futuresContractsToMarketRows(contracts) {
  if (!Array.isArray(contracts)) return [];
  return normalizeFuturesMarketPairs(
    contracts.filter(
      (c) => !c?.contract_category || String(c.contract_category).trim().toUpperCase() === "USD_M"
    )
  );
}

/** Apply a `futures:update` payload to market futures rows. */
export function applyFuturesUpdatePayload(data) {
  if (!data || typeof data !== "object") return null;

  if (data.type === "contracts_only" && Array.isArray(data.contracts)) {
    return futuresContractsToMarketRows(data.contracts);
  }

  return null;
}

/** Merge a live `contract_data` tick into the market futures list row. */
export function mergeFuturesMarketContractUpdate(rows, contract) {
  if (!contract?.symbol || !Array.isArray(rows) || rows.length === 0) return rows;
  const idx = rows.findIndex((p) => p.symbol === contract.symbol);
  if (idx < 0) return rows;
  const next = [...rows];
  next[idx] = normalizeFuturesMarketPair({ ...next[idx], ...contract });
  return next;
}
